/**
 * Project-level Voorziene kostenplaatsen — inline cell.
 *
 * Renders the right-aligned chips + an edit affordance, and hosts the
 * picker dialog with all building units + a per-unit "moet leeg" toggle.
 *
 * Used from the renovation project entity config so the row sits inside
 * the existing "Renovatieproject Details" card.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Cube, PencilSimple, Lock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ProjectProvisionalUnit } from "shared/types/renovation";
import { UnitDTO } from "shared/types/unit";

interface ProjectProvisionalUnitsCellProps {
  project: any;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export const ProjectProvisionalUnitsCell: React.FC<
  ProjectProvisionalUnitsCellProps
> = ({ project }) => {
  const { session } = useSession();
  const buildingId: string | undefined = project?.building?.id;

  // Local mirror of the persisted set so the cell can re-render on save
  // without waiting for the parent UniversalDetailPage to refetch.
  const [items, setItems] = useState<ProjectProvisionalUnit[]>(
    project?.provisionalUnits ?? [],
  );
  useEffect(() => {
    setItems(project?.provisionalUnits ?? []);
  }, [project?.provisionalUnits]);

  // Units that already live on a project part (either as provisional units
  // on a part or as cost-center distributions on its assignments). These are
  // shown alongside the project's own picks and are LOCKED — the user must
  // remove them from the part first before they can disappear here.
  const lockedUnitIds = useMemo(() => {
    const set = new Set<string>();
    const parts: any[] = Array.isArray(project?.parts) ? project.parts : [];
    for (const part of parts) {
      for (const pu of part?.provisionalUnits ?? []) {
        if (pu?.unit?.id) set.add(pu.unit.id);
      }
      for (const a of part?.investmentAssignments ?? []) {
        for (const d of a?.unitDistributions ?? []) {
          if (d?.unit?.id) set.add(d.unit.id);
        }
        if (!a?.unitDistributions?.length) {
          for (const u of a?.investmentItem?.units ?? []) {
            if (u?.id) set.add(u.id);
          }
        }
      }
    }
    return set;
  }, [project?.parts]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [buildingUnits, setBuildingUnits] = useState<UnitDTO[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [draft, setDraft] = useState<
    { unitId: string; mustBeEmpty: boolean }[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Seed dialog state when opened.
  useEffect(() => {
    if (!pickerOpen) return;
    setDraft(
      items.map((p) => ({
        unitId: p.unit.id,
        mustBeEmpty: !!p.mustBeEmpty,
      })),
    );
    setSearch("");
  }, [pickerOpen, items]);

  // Lazy-load building units the moment the dialog opens.
  useEffect(() => {
    if (!pickerOpen || !buildingId || !session) return;
    let cancelled = false;
    setLoadingUnits(true);
    fetchWithAuth(
      `${API_URL}/units/building/${buildingId}`,
      { method: "GET" },
      session,
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.data || [];
        setBuildingUnits(list);
      })
      .catch((err) => console.error("Error loading units:", err))
      .finally(() => {
        if (!cancelled) setLoadingUnits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, buildingId, session]);

  const filteredUnits = useMemo(() => {
    const withIds = buildingUnits.filter(
      (u): u is UnitDTO & { id: string } => !!u.id,
    );
    const term = search.trim().toLowerCase();
    if (!term) return withIds;
    return withIds.filter((u) => u.id.toLowerCase().includes(term));
  }, [buildingUnits, search]);

  const draftMap = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const d of draft) m.set(d.unitId, d.mustBeEmpty);
    return m;
  }, [draft]);

  const toggleSelected = (unitId: string) => {
    setDraft((prev) => {
      const exists = prev.some((d) => d.unitId === unitId);
      if (exists) return prev.filter((d) => d.unitId !== unitId);
      return [...prev, { unitId, mustBeEmpty: false }];
    });
  };

  // Select-all only operates on units the user can actually toggle —
  // locked units are managed via the project part and must stay untouched.
  const toggleableFilteredUnits = useMemo(
    () => filteredUnits.filter((u) => !lockedUnitIds.has(u.id)),
    [filteredUnits, lockedUnitIds],
  );

  const allFilteredSelected = useMemo(() => {
    if (toggleableFilteredUnits.length === 0) return false;
    return toggleableFilteredUnits.every((u) => draftMap.has(u.id));
  }, [toggleableFilteredUnits, draftMap]);

  const toggleSelectAll = () => {
    if (toggleableFilteredUnits.length === 0) return;
    setDraft((prev) => {
      const filteredIds = new Set(toggleableFilteredUnits.map((u) => u.id));
      if (allFilteredSelected) {
        return prev.filter((d) => !filteredIds.has(d.unitId));
      }
      const existing = new Set(prev.map((d) => d.unitId));
      const additions = toggleableFilteredUnits
        .filter((u) => !existing.has(u.id))
        .map((u) => ({ unitId: u.id, mustBeEmpty: false }));
      return [...prev, ...additions];
    });
  };

  const setMustBeEmpty = (unitId: string, value: boolean) => {
    setDraft((prev) =>
      prev.map((d) =>
        d.unitId === unitId ? { ...d, mustBeEmpty: value } : d,
      ),
    );
  };

  const handleSave = async () => {
    if (!project?.id || !session) return;
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/renovation/project/${project.id}/provisional-units`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ units: draft }),
        },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err.message || "Failed to update voorziene kostenplaatsen",
        );
      }
      const saved = await res.json();
      // The endpoint returns the new full list of links — mirror it
      // locally so the cell re-renders without a hard reload.
      const normalized: ProjectProvisionalUnit[] = Array.isArray(saved)
        ? saved
        : [];
      setItems(normalized);
      // Notify the page so it can re-fetch the full project (to update
      // huurderving / other derived fields). Listener is optional; if no
      // one is listening, the cell still reflects the new chips.
      window.dispatchEvent(
        new CustomEvent("renovation-project-refresh", {
          detail: { projectId: project.id },
        }),
      );
      toast.success("Voorziene kostenplaatsen bijgewerkt");
      setPickerOpen(false);
    } catch (err) {
      console.error("Error updating provisional units:", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Fout bij bijwerken voorziene kostenplaatsen",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Merged display: items the user picked + locked units already live on a
  // project part. Locked-only units render with a lock icon and can't be
  // removed from the picker until they're detached on the part.
  const projectItemIds = useMemo(
    () => new Set(items.map((i) => i.unit?.id).filter((id): id is string => !!id)),
    [items],
  );
  const lockedOnlyIds = useMemo(
    () => Array.from(lockedUnitIds).filter((id) => !projectItemIds.has(id)),
    [lockedUnitIds, projectItemIds],
  );
  const hasAnyChips = items.length > 0 || lockedOnlyIds.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        disabled={!buildingId}
        className="group flex items-center justify-end gap-2"
        title="Kostenplaatsen beheren"
      >
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {hasAnyChips ? (
            <>
              {items.map((link) => {
                const isLocked = lockedUnitIds.has(link.unit.id);
                return (
                  <span
                    key={link.id}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${
                      link.mustBeEmpty
                        ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-moss-primary/30 bg-moss-primary/10 text-moss-primary"
                    }`}
                    title={
                      isLocked
                        ? `${link.unit.id} — gekoppeld aan een projectonderdeel`
                        : link.mustBeEmpty
                        ? `${link.unit.id} — moet leeg zijn`
                        : link.unit.id
                    }
                  >
                    {isLocked ? (
                      <Lock className="h-3 w-3" weight="fill" />
                    ) : (
                      <Cube className="h-3 w-3" weight="duotone" />
                    )}
                    <span>{link.unit.id}</span>
                    {link.mustBeEmpty && (
                      <span className="text-[9px] uppercase tracking-wide">
                        leeg
                      </span>
                    )}
                  </span>
                );
              })}
              {lockedOnlyIds.map((unitId) => (
                <span
                  key={`locked-${unitId}`}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
                  title={`${unitId} — gekoppeld aan een projectonderdeel, kan niet hier verwijderd worden`}
                >
                  <Lock className="h-3 w-3" weight="fill" />
                  <span>{unitId}</span>
                </span>
              ))}
            </>
          ) : (
            <span className="text-neutral-secondary-mid text-xs italic">
              Klik om kostenplaatsen te kiezen
            </span>
          )}
        </div>
        <PencilSimple
          size={14}
          className="text-neutral-secondary-mid group-hover:text-moss-primary shrink-0"
          weight="duotone"
        />
      </button>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Voorziene kostenplaatsen</DialogTitle>
            <DialogDescription>
              Kies welke units dit project zullen raken en vink "moet
              leeg" aan voor de units die leeg moeten zijn vóór de start.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-1 flex-col gap-3 overflow-hidden">
            <div className="flex items-center gap-2">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op unit ID..."
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                disabled={
                  toggleableFilteredUnits.length === 0 || loadingUnits
                }
                className="shrink-0"
              >
                {allFilteredSelected
                  ? "Alles deselecteren"
                  : "Alles selecteren"}
              </Button>
            </div>

            <div className="flex-1 space-y-1 overflow-y-auto pr-1">
              {loadingUnits ? (
                <p className="text-neutral-secondary-mid py-6 text-center text-sm">
                  Units laden…
                </p>
              ) : filteredUnits.length === 0 ? (
                <p className="text-neutral-secondary-mid py-6 text-center text-sm">
                  Geen units gevonden
                </p>
              ) : (
                filteredUnits.map((unit) => {
                  const selected = draftMap.has(unit.id);
                  const mustBeEmpty = !!draftMap.get(unit.id);
                  const monthly = Number(unit.estimatedRentalValue || 0);
                  const isLocked = lockedUnitIds.has(unit.id);
                  return (
                    <div
                      key={unit.id}
                      className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                        isLocked
                          ? "border-amber-300 bg-amber-50/60"
                          : selected
                          ? "border-moss-primary/40 bg-moss-primary/5"
                          : "border-neutral-200 bg-white"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => !isLocked && toggleSelected(unit.id)}
                        disabled={isLocked}
                        className={`flex flex-1 items-center gap-2 text-left ${
                          isLocked ? "cursor-not-allowed" : ""
                        }`}
                        title={
                          isLocked
                            ? "Deze unit is al gekoppeld aan een projectonderdeel — verwijder hem daar eerst"
                            : undefined
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selected || isLocked}
                          readOnly
                          disabled={isLocked}
                          className={`h-4 w-4 ${
                            isLocked
                              ? "cursor-not-allowed text-amber-600"
                              : "text-moss-primary cursor-pointer"
                          }`}
                        />
                        {isLocked ? (
                          <Lock
                            size={14}
                            weight="fill"
                            className="text-amber-700"
                          />
                        ) : (
                          <Cube
                            size={14}
                            weight="duotone"
                            className={
                              selected
                                ? "text-moss-primary"
                                : "text-neutral-400"
                            }
                          />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            isLocked ? "text-amber-800" : ""
                          }`}
                        >
                          {unit.id}
                        </span>
                        {isLocked && (
                          <span className="text-[10px] uppercase tracking-wide text-amber-700">
                            Op projectonderdeel
                          </span>
                        )}
                        {monthly > 0 && (
                          <span className="text-xs text-neutral-500">
                            · {formatCurrency(monthly)}/maand
                          </span>
                        )}
                      </button>
                      {selected && !isLocked && (
                        <label className="flex items-center gap-2 text-xs text-neutral-600">
                          <Switch
                            checked={mustBeEmpty}
                            onCheckedChange={(v) =>
                              setMustBeEmpty(unit.id, v)
                            }
                          />
                          Moet leeg
                        </label>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPickerOpen(false)}
              disabled={isSubmitting}
            >
              Annuleren
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting}
              className="bg-moss-primary hover:bg-moss-primary/90 text-white"
            >
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectProvisionalUnitsCell;
